// components/PatientRegisterWizard.js
// The 5-screen patient registration flow (v2). Screen 2 (parent/account
// details) is shared across every path. Screen 3 is path-specific:
// Telehealth and Other-Lab-PGx now collect everything up front and finish
// by redirecting to a direct Stripe Checkout session (no emailed payment
// link, no separate post-login upload step) — Through-My-Clinician and
// Free-Conversion still finish through the original /register endpoint.
import React, { useContext, useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Stack,
  IconButton, Checkbox, FormControlLabel, MenuItem, Select,
  Radio, RadioGroup,
} from "@mui/material";
import {
  ArrowBack, LocalHospital, MedicalServices, UploadFile, Science,
  CheckCircle, MarkEmailRead,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";
import UserContext from "./UserContext";
import { TelehealthDetailsFields, PHENOTYPE_QUESTIONS, PHQ_QUESTIONS } from "./telehealthShared";
import { sanitizeNameInput, isValidName, sanitizePhoneInput, isValidPhone, isValidEmail } from "./validators";
import { PayItForwardDialog, PayItForwardExtra } from "./PayItForward";
import FavoriteIcon from "@mui/icons-material/Favorite";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const PENDING_LOGIN_KEY = "pendingTelehealthLogin";

// Stashed right before redirecting to Stripe's hosted checkout — the
// browser leaves this site entirely, so there's no other way to carry the
// freshly-minted JWT (and enough info to call setUser) across that
// round trip back to /register/patient?telehealth=success.
function stashPendingLogin(token, email, name) {
  if (!token) return;
  localStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify({ token, email, name }));
}

const BRAND = {
  navy: "#0a0e27", navyDark: "#0f1233", ink: "#0f1233", slate: "#5b6584",
  accent: "#637cdb", accentDark: "#3F6AC0", lightBg: "#eef1f8", border: "#e2e6f0",
};

const PATHS = [
  {
    key: "telehealth",
    icon: MedicalServices,
    title: "Get Tested via Telehealth (KidSafe)",
    desc: "For kids under 18. Complete a short intake, pay securely, and a licensed telehealth clinician reviews your order before your kit ships.",
    badge: "Kids",
  },
  {
    key: "through_clinician",
    icon: LocalHospital,
    title: "Test Through Your Current Clinician",
    desc: "Already working with a doctor? Select them below and we'll route your results straight to their care team.",
  },
  {
    key: "free_conversion",
    icon: UploadFile,
    title: "Upload Your Old Proove Results",
    desc: "Had a Proove test between 2009–2017? Upload your old results for a new, AI-powered report — free.",
    badge: "Free",
  },
  {
    key: "other_lab_upload",
    icon: Science,
    title: "Upload Another Lab's PGx Results",
    desc: "Have pharmacogenetics results from a different lab? Upload them and get Proove's AI-powered interpretation.",
    badge: "$99",
  },
];

const PANEL_CHOICES = [
  { key: "opioid_risk", label: "Proove Opioid Risk", cash: "$299" },
  { key: "pain_perception", label: "Proove Pain Perception", cash: "$199" },
  { key: "drug_metabolism", label: "Proove Drug Metabolism", cash: "$599" },
  { key: "all_panels", label: "All 3 Proove Panels", cash: "$799" },
];

function Field({ label, required, hint, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase" }}>
          {label}{required && <Box component="span" sx={{ color: BRAND.accent, ml: 0.4 }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: 12.5, color: sub }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

function StepProgress({ step, isDark }) {
  const labels = ["Path", "Account", "Details", "Extras", "Done"];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 4 }}>
      {labels.map((l, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <React.Fragment key={l}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
              <Box sx={{
                width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 800,
                bgcolor: done ? BRAND.accent : active ? BRAND.navy : (isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.06)"),
                color: done || active ? "#fff" : (isDark ? "#64748b" : "#94a3b8"),
                border: active ? `2px solid ${BRAND.accent}` : "none",
                transition: "all 0.3s",
              }}>
                {done ? <CheckCircle sx={{ fontSize: 16 }} /> : n}
              </Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
                color: active ? BRAND.accent : (isDark ? "#64748b" : "#94a3b8") }}>
                {l}
              </Typography>
            </Box>
            {i < labels.length - 1 && (
              <Box sx={{ flex: 1, height: 2, mt: -2.2, borderRadius: 1,
                bgcolor: done ? BRAND.accent : (isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"), transition: "all 0.3s" }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

const EMPTY_ADDRESS = { line1: "", city: "", state: "", zip: "" };
const EMPTY_CHILD = { first_name: "", last_name: "", date_of_birth: "", gender: "", race: "" };

export default function PatientRegisterWizard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);

  const bg = isDark ? "#0a0f1e" : "#f0f4ff";

  const [checkoutStatus, setCheckoutStatus] = useState(null); // "success" | "cancelled" | null
  const [checkoutKind, setCheckoutKind] = useState(null); // "telehealth" | "otherlab" | "panels" | null
  const [signedInAfterCheckout, setSignedInAfterCheckout] = useState(false);

  // Consumes the JWT stashed right before the Stripe redirect (see
  // stashPendingLogin) once the browser lands back here from checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // "panels" added 2026-08-17 — registration-time panel/subscription
    // purchases now redirect through Stripe the same way telehealth/
    // other-lab-PGx already did, see handleFinalSubmit's generic branch.
    const success = params.get("telehealth") === "success" || params.get("otherlab") === "success" || params.get("panels") === "success";
    const cancelled = params.get("telehealth") === "cancelled" || params.get("otherlab") === "cancelled" || params.get("panels") === "cancelled";
    if (!success && !cancelled) return;
    const kind = params.get("telehealth") ? "telehealth" : params.get("otherlab") ? "otherlab" : "panels";
    setCheckoutKind(kind);

    if (success) {
      const raw = localStorage.getItem(PENDING_LOGIN_KEY);
      if (raw) {
        try {
          const { token, email, name } = JSON.parse(raw);
          // force_password_change: true — the backend created this account
          // with a temporary password (emailed to the patient), so the
          // set-your-own-password step MUST still run. Previously this was
          // hardcoded false here and navigate("/home") ran immediately,
          // silently skipping that step — inconsistent with every other
          // way an account is created. Now: show the success screen (with
          // the "temporary password sent to your email" note), and its
          // Continue button lands on /home where ProtectedRoute redirects
          // to /change-password.
          // 2026-09-29 fix — payment_kind added: without it, ChangePassword.js
          // had no way to tell telehealth/otherlab/panels purchases apart and
          // always rendered its own hardcoded "Lab Test Copay and Activation
          // Fee Confirmed" text regardless of which one this actually was.
          // `kind` is already computed above (line ~161) for this exact
          // purpose on THIS page's own success screen — just never carried
          // any further than that.
          setUser({ email, name, token, role: "patient", force_password_change: true, payment_confirmed: true, payment_kind: kind });
          localStorage.removeItem(PENDING_LOGIN_KEY);
          setSignedInAfterCheckout(true);
          setCheckoutStatus("success");
          return;
        } catch {
          // fall through to showing the generic status message below
        }
      }
    }
    setCheckoutStatus(success ? "success" : "cancelled");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step, setStep] = useState(1);
  const [path, setPath] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [loadingClinicians, setLoadingClinicians] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [done, setDone] = useState(null);

  const [account, setAccount] = useState({ first_name: "", last_name: "", email: "", phone: "", date_of_birth: "" });
  const [clinicianId, setClinicianId] = useState("");
  const [panelChoice, setPanelChoice] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bill_later");
  const [subscriptionOptin, setSubscriptionOptin] = useState(false);
  const [payItForwardQty, setPayItForwardQty] = useState(0);
  const [pifDialogOpen, setPifDialogOpen] = useState(false);

  // Telehealth-specific state
  const [child, setChild] = useState(EMPTY_CHILD);
  const [shippingAddress, setShippingAddress] = useState(EMPTY_ADDRESS);
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [phenotypeAnswers, setPhenotypeAnswers] = useState({});
  const [phqAnswers, setPhqAnswers] = useState({});
  const [medications, setMedications] = useState([]);

  // Other-lab-upload state
  const [otherLabFile, setOtherLabFile] = useState(null);
  // 2026-09-27 addition — same is_for_child distinction telehealth already
  // has. Minimal fields (name + DOB only) — this flow needs enough to build
  // a distinct patient identity, nothing more (no address/questionnaire).
  const [otherLabIsForChild, setOtherLabIsForChild] = useState(false);
  const [otherLabChild, setOtherLabChild] = useState({ first_name: "", last_name: "", date_of_birth: "" });

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.018)",
      fontSize: 15,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(10,14,39,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
    },
    "& .MuiOutlinedInput-input": { py: "12px", px: "14px" },
  };

  useEffect(() => {
    if (path === "through_clinician" && clinicians.length === 0) {
      setLoadingClinicians(true);
      axios.get(`${API_BASE}/clinicians/directory`)
        .then((res) => setClinicians(res.data?.clinicians || []))
        .catch(() => setClinicians([]))
        .finally(() => setLoadingClinicians(false));
    }
  }, [path, clinicians.length]);

  const needsPaymentMethod = path === "through_clinician";
  const needsPanelChoice = needsPaymentMethod;

  const isUnder18 = (dob) => {
    if (!dob) return false;
    const born = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - born.getFullYear();
    const m = today.getMonth() - born.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < born.getDate())) age--;
    return age < 18;
  };

  // date_of_birth is required for EVERY path (2026-08-17) — same as
  // first/last name, email, and phone: a core identity field needed
  // regardless of registration path, not just telehealth/other_lab_upload.
  // (For telehealth specifically it's the PARENT's own DOB, not the
  // child's — needed so their GCS report folder,
  // patient_reports/{name}_{dob}/..., doesn't fall back to "unknown_dob"
  // and collide with another same-named parent; that risk applies equally
  // to through_clinician/free_conversion patients, which is why this is
  // now universal rather than conditional.)
  const canContinueStep2 =
    isValidName(account.first_name) && isValidName(account.last_name) &&
    isValidEmail(account.email) &&
    isValidPhone(account.phone) &&
    account.date_of_birth;

  const canContinueStep3 = (() => {
    if (path === "telehealth") {
      const childOk = isValidName(child.first_name) && isValidName(child.last_name) && child.date_of_birth && isUnder18(child.date_of_birth) && child.gender && child.race;
      const shipOk = shippingAddress.line1.trim() && shippingAddress.city.trim() && shippingAddress.state.trim() && shippingAddress.zip.trim();
      const billOk = sameAsShipping || (billingAddress.line1.trim() && billingAddress.city.trim() && billingAddress.state.trim() && billingAddress.zip.trim());
      const phenoOk = PHENOTYPE_QUESTIONS.every((q) => phenotypeAnswers[q]);
      const phqOk = PHQ_QUESTIONS.every((q) => phqAnswers[q]);
      return childOk && shipOk && billOk && phenoOk && phqOk;
    }
    if (path === "through_clinician") return panelChoice && clinicianId;
    if (path === "other_lab_upload") {
      if (!otherLabFile) return false;
      if (!otherLabIsForChild) return true;
      return isValidName(otherLabChild.first_name) && isValidName(otherLabChild.last_name) && Boolean(otherLabChild.date_of_birth) && isUnder18(otherLabChild.date_of_birth);
    }
    return true; // free_conversion — informational only
  })();

  const back = () => setStep((s) => Math.max(1, s - 1));
  const next = () => setStep((s) => Math.min(5, s + 1));

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setMessage(null);
    try {
      if (path === "telehealth") {
        const payload = {
          parent: {
            first_name: account.first_name.trim(), last_name: account.last_name.trim(),
            email: account.email.trim().toLowerCase(), phone: account.phone.trim(),
            date_of_birth: account.date_of_birth,
          },
          child: {
            first_name: child.first_name.trim(), last_name: child.last_name.trim(),
            date_of_birth: child.date_of_birth, gender: child.gender, race: child.race,
          },
          shipping_address: shippingAddress,
          billing_address: sameAsShipping ? shippingAddress : billingAddress,
          questionnaire: {
            phenotype_answers: phenotypeAnswers,
            phq_answers: phqAnswers,
            medications: medications.filter((m) => m.medication.trim()),
          },
          subscription_optin: subscriptionOptin,
          pay_it_forward_quantity: payItForwardQty,
        };
        const res = await axios.post(`${API_BASE}/telehealth/orders`, payload);
        stashPendingLogin(res.data.token, account.email.trim().toLowerCase(), `${account.first_name} ${account.last_name}`.trim());
        window.location.href = res.data.checkout_url;
        return;
      }

      if (path === "other_lab_upload") {
        const form = new FormData();
        form.append("file", otherLabFile);
        form.append("first_name", account.first_name.trim());
        form.append("last_name", account.last_name.trim());
        form.append("email", account.email.trim().toLowerCase());
        form.append("phone", account.phone.trim());
        form.append("date_of_birth", account.date_of_birth);
        form.append("subscription_optin", subscriptionOptin ? "true" : "false");
        form.append("is_for_child", otherLabIsForChild ? "true" : "false");
        if (otherLabIsForChild) {
          form.append("child_first_name", otherLabChild.first_name.trim());
          form.append("child_last_name", otherLabChild.last_name.trim());
          form.append("child_date_of_birth", otherLabChild.date_of_birth);
        }
        const res = await axios.post(`${API_BASE}/other-lab/orders`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        stashPendingLogin(res.data.token, account.email.trim().toLowerCase(), `${account.first_name} ${account.last_name}`.trim());
        window.location.href = res.data.checkout_url;
        return;
      }

      const payload = {
        first_name: account.first_name.trim(),
        last_name: account.last_name.trim(),
        email: account.email.trim().toLowerCase(),
        role: "patient",
        phone: account.phone.trim(),
        date_of_birth: account.date_of_birth || undefined,
        registration_path: path,
        subscription_optin: subscriptionOptin,
        panel_selections: [],
      };
      if (needsPaymentMethod) {
        payload.payment_method = paymentMethod;
        if (paymentMethod === "cash" && panelChoice) payload.panel_selections = [panelChoice];
        else payload.copay_preference = paymentMethod === "bill_now" ? "now" : "later";
      }
      if (path === "through_clinician") payload.clinician_id = clinicianId;

      const res = await axios.post(`${API_BASE}/register`, payload);
      // 2026-08-17: if panels/subscription were selected, /register now
      // starts direct checkout for them (same redirect pattern as
      // telehealth/other_lab_upload above) instead of emailing Payment
      // Links — copay (if any) is still emailed separately, unaffected.
      if (res.data.checkout_url) {
        stashPendingLogin(res.data.token, account.email.trim().toLowerCase(), `${account.first_name} ${account.last_name}`.trim());
        window.location.href = res.data.checkout_url;
        return;
      }
      setDone(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "pending_registration") {
        setMessage({
          severity: "warning",
          text: (
            <>
              {detail.message}{" "}
              <Box component={Link} to="/" sx={{ color: "inherit", fontWeight: 700, textDecoration: "underline" }}>
                Log in
              </Box>
            </>
          ),
        });
      } else if (detail && typeof detail === "object" && detail.code === "account_exists_please_login") {
        // 2026-09-27 addition — telehealth/other-lab orders now hard-stop a
        // guest submission whose email matches an existing account (see
        // _get_or_create_parent, backend main.py), rather than silently
        // continuing as a guest — same "log in" prompt pattern as
        // pending_registration above, not a generic failure message.
        setMessage({
          severity: "warning",
          text: (
            <>
              {detail.message}{" "}
              <Box component={Link} to="/" sx={{ color: "inherit", fontWeight: 700, textDecoration: "underline" }}>
                Log in
              </Box>
            </>
          ),
        });
      } else {
        setMessage({ severity: "error", text: (typeof detail === "string" ? detail : detail?.message) || "Registration failed. Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (checkoutStatus) {
    // Tailored per checkout kind (2026-09-03) — was one generic message
    // reused for telehealth, other-lab uploads, and panel purchases alike.
    const SUCCESS_COPY = {
      telehealth: "Your KidSafe Telehealth Kit order is confirmed.",
      otherlab: "Your lab report upload is confirmed and received.",
      panels: "Your panel purchase is confirmed.",
    };
    const CANCELLED_COPY = {
      telehealth: "Your payment for the KidSafe Telehealth Kit didn't go through, so this order wasn't placed. Your account was still created and your login details are on their way to your email — sign in and try checkout again whenever you're ready.",
      otherlab: "Your payment didn't go through, so your lab report upload wasn't completed. Your account was still created and your login details are on their way to your email — sign in and try again whenever you're ready.",
      panels: "Your payment didn't go through, so this panel purchase wasn't completed. Your account was still created and your login details are on their way to your email — sign in and try again whenever you're ready.",
    };
    return (
      <WizardShell bg={bg} isDark={isDark} navigate={navigate}>
        <Box sx={{ textAlign: "center", py: 2 }}>
          {checkoutStatus === "success" ? (
            <>
              <MarkEmailRead sx={{ fontSize: 52, color: BRAND.accent, mb: 2 }} />
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1 }}>
                Payment successful
              </Typography>
              <Typography sx={{ fontSize: 14.5, color: isDark ? "#94a3b8" : "#64748b", lineHeight: 1.7, maxWidth: 400, mx: "auto", mb: 2 }}>
                {SUCCESS_COPY[checkoutKind] || SUCCESS_COPY.telehealth}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#475569", lineHeight: 1.7, maxWidth: 400, mx: "auto", mb: 3, fontWeight: 600 }}>
                A temporary password has been sent to your email. {signedInAfterCheckout
                  ? "Continue to set your own password."
                  : "Use it to sign in, then you'll be prompted to set your own."}
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1 }}>
                Checkout cancelled
              </Typography>
              <Typography sx={{ fontSize: 14.5, color: isDark ? "#94a3b8" : "#64748b", lineHeight: 1.7, maxWidth: 380, mx: "auto", mb: 3 }}>
                {CANCELLED_COPY[checkoutKind] || CANCELLED_COPY.telehealth}
              </Typography>
            </>
          )}
          {checkoutStatus === "success" && signedInAfterCheckout ? (
            <Button onClick={() => navigate("/home", { replace: true })} variant="contained" sx={{
              mt: 1, borderRadius: "12px", py: 1.4, px: 4, fontWeight: 700,
              background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.accentDark})`,
            }}>
              Continue
            </Button>
          ) : (
            <Button component={Link} to="/" variant="contained" sx={{
              mt: 1, borderRadius: "12px", py: 1.4, px: 4, fontWeight: 700,
              background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.accentDark})`,
            }}>
              Go to Login
            </Button>
          )}
        </Box>
      </WizardShell>
    );
  }

  if (done) {
    return (
      <WizardShell bg={bg} isDark={isDark} navigate={navigate}>
        <Box sx={{ textAlign: "center", py: 2 }}>
          <MarkEmailRead sx={{ fontSize: 52, color: BRAND.accent, mb: 2 }} />
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1 }}>
            Check your email
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: isDark ? "#94a3b8" : "#64748b", lineHeight: 1.7, maxWidth: 380, mx: "auto", mb: 3 }}>
            {done.message || "Your account is ready — your login credentials and payment link (if applicable) have been sent to your email and phone."}
          </Typography>
          {path === "free_conversion" && (
            <Alert severity="info" sx={{ borderRadius: 2, textAlign: "left", mb: 2 }}>
              Once you log in, you'll be able to convert up to 3 of your old Proove reports (Opioid Risk, Pain Perception, PGX) for free — pick any or all of them, in any order, whenever you're ready.
            </Alert>
          )}
          <Button component={Link} to="/" variant="contained" sx={{
            mt: 1, borderRadius: "12px", py: 1.4, px: 4, fontWeight: 700,
            background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.accentDark})`,
          }}>
            Go to Login
          </Button>
        </Box>
      </WizardShell>
    );
  }

  return (
    <WizardShell bg={bg} isDark={isDark} navigate={navigate}>
      <StepProgress step={step} isDark={isDark} />

      <AnimatePresence mode="wait">
        {message && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>
              {message.text}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.5 }}>
              How would you like to get tested?
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", mb: 3 }}>
              Pick one to continue — you can always do the others later from your account.
            </Typography>
            <Stack spacing={1.5}>
              {PATHS.map(({ key, icon: Icon, title, desc, badge }) => {
                const active = path === key;
                return (
                  <motion.div key={key} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                    <Box onClick={() => { setPath(key); setStep(2); }} sx={{
                      cursor: "pointer", borderRadius: "14px", p: 2.25, display: "flex", gap: 2, alignItems: "flex-start",
                      border: `2px solid ${active ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.12)")}`,
                      bgcolor: active ? (isDark ? "rgba(99,124,219,0.1)" : "rgba(99,124,219,0.05)") : (isDark ? "rgba(255,255,255,0.02)" : "#fff"),
                      transition: "all 0.18s",
                      "&:hover": { borderColor: BRAND.accent },
                    }}>
                      <Box sx={{ width: 42, height: 42, borderRadius: "10px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: isDark ? "rgba(99,124,219,0.14)" : BRAND.lightBg }}>
                        <Icon sx={{ fontSize: 22, color: BRAND.accent }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy }}>{title}</Typography>
                          {badge && (
                            <Box sx={{ fontSize: 10.5, fontWeight: 800, px: 0.9, py: 0.15, borderRadius: "6px",
                              bgcolor: badge === "Free" ? "rgba(34,197,94,0.15)" : "rgba(99,124,219,0.15)",
                              color: badge === "Free" ? "#16a34a" : BRAND.accentDark }}>
                              {badge}
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 12.5, color: isDark ? "#94a3b8" : "#64748b", mt: 0.3, lineHeight: 1.5 }}>{desc}</Typography>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
              <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                <Box onClick={() => setPifDialogOpen(true)} sx={{
                  cursor: "pointer", borderRadius: "14px", p: 2.25, display: "flex", gap: 2, alignItems: "flex-start",
                  border: `2px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.12)"}`,
                  bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#fff",
                  transition: "all 0.18s",
                  "&:hover": { borderColor: "#e11d48" },
                }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: "10px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: isDark ? "rgba(225,29,72,0.14)" : "rgba(225,29,72,0.08)" }}>
                    <FavoriteIcon sx={{ fontSize: 20, color: "#e11d48" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                        Pay It Forward — Sponsor a KidSafe Test
                      </Typography>
                      <Box sx={{ fontSize: 10.5, fontWeight: 800, px: 0.9, py: 0.15, borderRadius: "6px",
                        bgcolor: "rgba(225,29,72,0.15)", color: "#e11d48" }}>
                        Donate
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: isDark ? "#94a3b8" : "#64748b", mt: 0.3, lineHeight: 1.5 }}>
                      No test for you — just $299 to cover one KidSafe kit for a family who can't afford one. Choose how many.
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Stack>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.5 }}>
              {path === "telehealth" ? "Let's get your (parent/guardian) account set up" : "Let's get your account set up"}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", mb: 3 }}>
              {path === "telehealth" ? "You'll add your child's details on the next screen." : "Same for everyone, regardless of how you're getting tested."}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Field label="First name" required isDark={isDark}>
                <TextField hiddenLabel fullWidth placeholder="Jane" sx={inputSx}
                  value={account.first_name} onChange={(e) => setAccount((p) => ({ ...p, first_name: sanitizeNameInput(e.target.value) }))} />
              </Field>
              <Field label="Last name" required isDark={isDark}>
                <TextField hiddenLabel fullWidth placeholder="Doe" sx={inputSx}
                  value={account.last_name} onChange={(e) => setAccount((p) => ({ ...p, last_name: sanitizeNameInput(e.target.value) }))} />
              </Field>
            </Box>
            <Field label="Email" required isDark={isDark}>
              <TextField hiddenLabel type="email" fullWidth placeholder="jane@example.com" sx={inputSx}
                value={account.email} onChange={(e) => setAccount((p) => ({ ...p, email: e.target.value }))} />
            </Field>
            <Field label="Phone number" required hint="For your login credentials" isDark={isDark}>
              <TextField hiddenLabel type="tel" fullWidth placeholder="(555) 123-4567" sx={inputSx}
                value={account.phone} onChange={(e) => setAccount((p) => ({ ...p, phone: sanitizePhoneInput(e.target.value) }))} />
            </Field>
            <Field
              label="Date of birth"
              required
              hint={path === "telehealth" ? "Your own date of birth, not your child's" : undefined}
              isDark={isDark}
            >
              <TextField hiddenLabel type="date" fullWidth sx={inputSx}
                value={account.date_of_birth} onChange={(e) => setAccount((p) => ({ ...p, date_of_birth: e.target.value }))} />
            </Field>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            <Step3Content
              path={path} isDark={isDark} inputSx={inputSx}
              panelChoice={panelChoice} setPanelChoice={setPanelChoice}
              clinicianId={clinicianId} setClinicianId={setClinicianId}
              clinicians={clinicians} loadingClinicians={loadingClinicians}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              child={child} setChild={setChild} isUnder18={isUnder18}
              shippingAddress={shippingAddress} setShippingAddress={setShippingAddress}
              billingAddress={billingAddress} setBillingAddress={setBillingAddress}
              sameAsShipping={sameAsShipping} setSameAsShipping={setSameAsShipping}
              phenotypeAnswers={phenotypeAnswers} setPhenotypeAnswers={setPhenotypeAnswers}
              phqAnswers={phqAnswers} setPhqAnswers={setPhqAnswers}
              medications={medications} setMedications={setMedications}
              otherLabFile={otherLabFile} setOtherLabFile={setOtherLabFile}
              otherLabIsForChild={otherLabIsForChild} setOtherLabIsForChild={setOtherLabIsForChild}
              otherLabChild={otherLabChild} setOtherLabChild={setOtherLabChild}
            />
          </motion.div>
        )}

        {step === 4 && (
          <motion.div key="s4" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.5 }}>
              {path === "telehealth" || path === "other_lab_upload" ? "Almost there" : "Want ongoing AI updates?"}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", mb: 3 }}>
              {path === "telehealth" || path === "other_lab_upload"
                ? "You'll pay securely on the next screen. Add the optional monthly update below, or skip it and continue to recap and checkout."
                : "Totally optional — skip it now and add it anytime from your account."}
            </Typography>
            <Box onClick={() => setSubscriptionOptin((v) => !v)} sx={{
              cursor: "pointer", borderRadius: "14px", p: 2.5, display: "flex", gap: 2, alignItems: "flex-start",
              border: `2px solid ${subscriptionOptin ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.12)")}`,
              bgcolor: subscriptionOptin ? (isDark ? "rgba(99,124,219,0.1)" : "rgba(99,124,219,0.05)") : "transparent",
              transition: "all 0.18s",
            }}>
              <Checkbox checked={subscriptionOptin} sx={{ p: 0, mt: 0.3 }} onClick={(e) => e.stopPropagation()} onChange={() => setSubscriptionOptin((v) => !v)} />
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                  $0.99/month — Ongoing AI Report Updates
                </Typography>
                <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, lineHeight: 1.6 }}>
                  Every month, we check for genuinely new, verified research relevant to your saved results and
                  add it to your report history — sourced, so you can check it yourself. Cancel anytime.
                </Typography>
              </Box>
            </Box>
            {path === "telehealth" && (
              <PayItForwardExtra quantity={payItForwardQty} setQuantity={setPayItForwardQty} isDark={isDark} accentColor={BRAND.accent} />
            )}
          </motion.div>
        )}

        {step === 5 && (
          <motion.div key="s5" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.5 }}>
              {path === "telehealth" || path === "other_lab_upload" ? "Ready to check out?" : "Ready to go?"}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", mb: 3 }}>
              {path === "telehealth" || path === "other_lab_upload"
                ? "Here's a quick recap — you'll complete payment securely on Stripe's checkout page."
                : "Here's a quick recap before we create your account."}
            </Typography>
            <Box sx={{ borderRadius: "14px", border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.1)"}`, p: 2.5, mb: 1 }}>
              <RecapRow isDark={isDark} label="Path" value={PATHS.find((p) => p.key === path)?.title} />
              <RecapRow isDark={isDark} label="Name" value={`${account.first_name} ${account.last_name}`} />
              <RecapRow isDark={isDark} label="Email" value={account.email} />
              {path === "telehealth" && (
                <>
                  <RecapRow isDark={isDark} label="Child" value={`${child.first_name} ${child.last_name}`} />
                  <RecapRow isDark={isDark} label="Test" value="Opioid Risk + Drug Metabolism (KidSafe)" />
                </>
              )}
              {path === "telehealth" && (
                <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, fontSize: 12.5 }}>
                  On Stripe's payment page, enter promo code <b>KIDSAFE599</b> in the "Add promotion code" box
                  to apply your KidSafe discount — it isn't applied automatically.
                </Alert>
              )}
              {path === "other_lab_upload" && (
                <>
                  <RecapRow isDark={isDark} label="File" value={otherLabFile?.name} />
                  <RecapRow isDark={isDark} label="For"
                    value={otherLabIsForChild ? `${otherLabChild.first_name} ${otherLabChild.last_name} (child)` : "You"} />
                </>
              )}
              {needsPanelChoice && panelChoice && (
                <RecapRow isDark={isDark} label="Testing for" value={PANEL_CHOICES.find((p) => p.key === panelChoice)?.label} />
              )}
              {path === "through_clinician" && clinicianId && (
                <RecapRow isDark={isDark} label="Clinician" value={clinicians.find((c) => c.id === clinicianId)?.display_name} />
              )}
              {needsPaymentMethod && (
                <RecapRow isDark={isDark} label="Payment"
                  value={paymentMethod === "bill_now" ? "Bill insurance, pay $25 copay now" : paymentMethod === "bill_later" ? "Bill insurance, pay copay later" : "Pay cash, skip insurance"} />
              )}
              <RecapRow isDark={isDark} label="Monthly AI updates" value={subscriptionOptin ? "Yes, $0.99/mo" : "Not now"}
                last={!(path === "telehealth" && payItForwardQty > 0)} />
              {path === "telehealth" && payItForwardQty > 0 && (
                <RecapRow isDark={isDark} label="Pay It Forward" value={`${payItForwardQty} kit${payItForwardQty > 1 ? "s" : ""} — $${(payItForwardQty * 299).toLocaleString()}`} last />
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <PayItForwardDialog open={pifDialogOpen} onClose={() => setPifDialogOpen(false)} isDark={isDark} accentColor={BRAND.accent} navyColor={BRAND.navy} />

      <Stack direction="row" spacing={1.5} sx={{ mt: 4 }}>
        {step > 1 && (
          <Button onClick={back} disabled={submitting} sx={{
            borderRadius: "12px", fontWeight: 700, py: 1.3, px: 3,
            color: isDark ? "#94a3b8" : "#64748b",
          }}>
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {step === 2 && (
          <Button variant="contained" disabled={!canContinueStep2} onClick={next} sx={{
            borderRadius: "12px", fontWeight: 700, py: 1.3, px: 4,
            background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.accentDark})`,
          }}>
            Continue
          </Button>
        )}
        {step === 3 && (
          <Button variant="contained" disabled={!canContinueStep3} onClick={next} sx={{
            borderRadius: "12px", fontWeight: 700, py: 1.3, px: 4,
            background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.accentDark})`,
          }}>
            Continue
          </Button>
        )}
        {step === 4 && (
          <Button variant="contained" onClick={next} sx={{
            borderRadius: "12px", fontWeight: 700, py: 1.3, px: 4,
            background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.accentDark})`,
          }}>
            Continue
          </Button>
        )}
        {step === 5 && (
          <Button variant="contained" disabled={submitting} onClick={handleFinalSubmit} startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null} sx={{
            borderRadius: "12px", fontWeight: 700, py: 1.3, px: 4,
            background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.accentDark})`,
          }}>
            {submitting
              ? "Processing…"
              : (path === "telehealth" || path === "other_lab_upload") ? "Continue to Payment" : "Complete Registration"}
          </Button>
        )}
      </Stack>
    </WizardShell>
  );
}

function RecapRow({ label, value, isDark, last }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: last ? "none" : `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.06)"}` }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: isDark ? "#f1f5f9" : BRAND.navy, textAlign: "right" }}>{value || "—"}</Typography>
    </Box>
  );
}

function PanelChoiceField({ isDark, panelChoice, setPanelChoice, cashOnly }) {
  return (
    <Field label="Which test?" required isDark={isDark}>
      <RadioGroup value={panelChoice} onChange={(e) => setPanelChoice(e.target.value)}>
        {PANEL_CHOICES.map((p) => (
          <FormControlLabel key={p.key} value={p.key} control={<Radio size="small" />}
            label={
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", minWidth: 280 }}>
                <Typography sx={{ fontSize: 13.5, color: isDark ? "#e2e8f0" : "#1e293b" }}>{p.label}</Typography>
                {cashOnly && <Typography sx={{ fontSize: 12.5, color: BRAND.accent, fontWeight: 700 }}>{p.cash}</Typography>}
              </Box>
            }
            sx={{ mb: 0.5, "& .MuiFormControlLabel-label": { width: "100%" } }}
          />
        ))}
      </RadioGroup>
    </Field>
  );
}

const PAYMENT_METHOD_OPTIONS = [
  { key: "bill_now", label: "Bill insurance, pay $25 copay now", sub: "Charged immediately at registration" },
  { key: "bill_later", label: "Bill insurance, pay copay later", sub: "We'll send a secure link whenever you're ready" },
  { key: "cash", label: "Pay cash instead, skip insurance", sub: "No insurance billing at all" },
];

function PaymentMethodField({ isDark, paymentMethod, setPaymentMethod }) {
  return (
    <Field label="How would you like to pay?" required isDark={isDark}>
      <Stack spacing={1}>
        {PAYMENT_METHOD_OPTIONS.map((opt) => {
          const active = paymentMethod === opt.key;
          return (
            <Box key={opt.key} onClick={() => setPaymentMethod(opt.key)} sx={{
              cursor: "pointer", borderRadius: "10px", px: 1.75, py: 1.2,
              border: `2px solid ${active ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.12)")}`,
              bgcolor: active ? (isDark ? "rgba(99,124,219,0.1)" : "rgba(99,124,219,0.05)") : "transparent",
              transition: "all 0.15s",
              "&:hover": { borderColor: BRAND.accent },
            }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                {opt.label}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: isDark ? "#94a3b8" : "#64748b", mt: 0.2 }}>
                {opt.sub}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Field>
  );
}


function Step3Content({
  path, isDark, inputSx, panelChoice, setPanelChoice, clinicianId, setClinicianId,
  clinicians, loadingClinicians, paymentMethod, setPaymentMethod,
  child, setChild, isUnder18,
  shippingAddress, setShippingAddress, billingAddress, setBillingAddress,
  sameAsShipping, setSameAsShipping,
  phenotypeAnswers, setPhenotypeAnswers, phqAnswers, setPhqAnswers,
  medications, setMedications,
  otherLabFile, setOtherLabFile,
  otherLabIsForChild, setOtherLabIsForChild, otherLabChild, setOtherLabChild,
}) {
  if (path === "telehealth") {
    return (
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 3 }}>Child &amp; order details</Typography>
        <TelehealthDetailsFields
          isDark={isDark} inputSx={inputSx} accentColor={BRAND.accent}
          child={child} setChild={setChild}
          shippingAddress={shippingAddress} setShippingAddress={setShippingAddress}
          billingAddress={billingAddress} setBillingAddress={setBillingAddress}
          sameAsShipping={sameAsShipping} setSameAsShipping={setSameAsShipping}
          phenotypeAnswers={phenotypeAnswers} setPhenotypeAnswers={setPhenotypeAnswers}
          phqAnswers={phqAnswers} setPhqAnswers={setPhqAnswers}
          medications={medications} setMedications={setMedications}
        />
      </Box>
    );
  }

  if (path === "through_clinician") {
    return (
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 3 }}>Your clinician</Typography>
        <Field label="Select your clinician" required isDark={isDark}>
          {loadingClinicians ? (
            <CircularProgress size={20} />
          ) : clinicians.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13 }}>
              No registered clinicians found yet. Ask your doctor's office to register with Proove, or choose a different testing option.
            </Alert>
          ) : (
            <Select size="small" fullWidth displayEmpty value={clinicianId} onChange={(e) => setClinicianId(e.target.value)} sx={inputSx}>
              <MenuItem value="" disabled>Choose your clinician…</MenuItem>
              {clinicians.map((c) => <MenuItem key={c.id} value={c.id}>{c.display_name}</MenuItem>)}
            </Select>
          )}
        </Field>
        <PanelChoiceField isDark={isDark} panelChoice={panelChoice} setPanelChoice={setPanelChoice} cashOnly={paymentMethod === "cash"} />
        <PaymentMethodField isDark={isDark} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} />
      </Box>
    );
  }

  if (path === "free_conversion") {
    return (
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1.5 }}>Nothing more needed here</Typography>
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Once you log in, you'll see three options — Opioid Risk, Pain Perception, and PGX — each free to convert
          once. Pick any or all of them, whenever you're ready. Your old report stays safely in your history either way.
        </Alert>
      </Box>
    );
  }

  // other_lab_upload
  return (
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1.5 }}>Upload your report</Typography>
      <Alert severity="info" sx={{ borderRadius: 2, mb: 2.5 }}>
        $99, charged per upload (not a one-time fee). Upload the PDF, then pay securely — we'll let you know once it's ready.
      </Alert>
      <Field label="Lab report (PDF)" required isDark={isDark}>
        <Button component="label" variant="outlined" fullWidth sx={{ py: 1.5, textTransform: "none", borderRadius: "10px" }}>
          {otherLabFile ? otherLabFile.name : "Choose file…"}
          <input type="file" accept="application/pdf" hidden onChange={(e) => setOtherLabFile(e.target.files?.[0] || null)} />
        </Button>
      </Field>

      {/* 2026-09-27 addition — same is_for_child distinction telehealth
          already has. */}
      <FormControlLabel
        sx={{ mt: 1, mb: 1 }}
        control={
          <Checkbox
            checked={otherLabIsForChild}
            onChange={(e) => setOtherLabIsForChild(e.target.checked)}
          />
        }
        label="This report is for my child, not myself"
      />
      {otherLabIsForChild && (
        <Field label="Child's details" required isDark={isDark}>
          <Stack direction="row" spacing={1.5}>
            <TextField size="small" fullWidth sx={inputSx} placeholder="First name"
              value={otherLabChild.first_name}
              onChange={(e) => setOtherLabChild((prev) => ({ ...prev, first_name: sanitizeNameInput(e.target.value) }))} />
            <TextField size="small" fullWidth sx={inputSx} placeholder="Last name"
              value={otherLabChild.last_name}
              onChange={(e) => setOtherLabChild((prev) => ({ ...prev, last_name: sanitizeNameInput(e.target.value) }))} />
            <TextField size="small" fullWidth sx={inputSx} type="date" InputLabelProps={{ shrink: true }}
              value={otherLabChild.date_of_birth}
              onChange={(e) => setOtherLabChild((prev) => ({ ...prev, date_of_birth: e.target.value }))} />
          </Stack>
          {otherLabChild.date_of_birth && !isUnder18(otherLabChild.date_of_birth) && (
            <Alert severity="warning" sx={{ borderRadius: 2, mt: 1, fontSize: 13 }}>
              The child option is only for patients under 18. For yourself or another adult, uncheck the box above.
            </Alert>
          )}
        </Field>
      )}
    </Box>
  );
}

function WizardShell({ children, bg, isDark, navigate }) {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column" }}>
      <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(10,14,39,0.97)", backdropFilter: "blur(16px)", borderBottom: "2px solid rgba(99,124,219,0.3)" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton onClick={() => navigate("/")} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
              <ArrowBack sx={{ fontSize: 20 }} />
            </IconButton>
            <Box component={Link} to="/" sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-light.png" alt="Proove" style={{ height: 20, width: "auto" }} />
            </Box>
          </Box>
          <ThemeToggle />
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", px: 3, py: 6 }}>
        <Box sx={{ width: "100%", maxWidth: 600, borderRadius: 3, overflow: "hidden",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(10,14,39,0.12)"}`,
          boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5), 0 0 50px -18px rgba(99,124,219,0.45)" : "0 24px 80px rgba(10,14,39,0.12), 0 0 40px -20px rgba(99,124,219,0.3)",
          bgcolor: isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.85)",
          backdropFilter: "blur(20px) saturate(140%)" }}>
          <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.accent})` }} />
          <Box sx={{ px: 4, py: 4 }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}
